import { useState, useMemo } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useReminders } from '@/hooks/useReminders';

function AddReminderModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { task: string; dueDate: string; dueTime: string; isUrgent: boolean }) => void;
}) {
  const { isDark } = useTheme();
  const [task, setTask] = useState('');
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dueTime, setDueTime] = useState('10:00');
  const [isUrgent, setIsUrgent] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!task.trim()) {
      setError('Görev açıklaması zorunludur.');
      return;
    }
    onSubmit({ task: task.trim(), dueDate, dueTime, isUrgent });
    setTask('');
    setDueDate(new Date().toISOString().split('T')[0]);
    setDueTime('10:00');
    setIsUrgent(false);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-base font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Yeni Hatırlatma</h3>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-full ${isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'}`}>
            <i className="ri-close-line" />
          </button>
        </div>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="space-y-3">
          <textarea
            value={task}
            onChange={(e) => setTask(e.target.value)}
            placeholder="Görev açıklaması (örn: Fatma Kaya - Dönüş yapılacak)"
            maxLength={500}
            rows={3}
            className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none resize-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted' : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted'}`}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-xs block mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Tarih</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'}`}
              />
            </div>
            <div>
              <label className={`text-xs block mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Saat</label>
              <input
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-dark-bg border-dark-border text-dark-text' : 'bg-light-bg border-light-border text-light-text'}`}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isUrgent} onChange={(e) => setIsUrgent(e.target.checked)} className="w-4 h-4 accent-primary-500" />
            <span className={`text-sm ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Acil</span>
          </label>
        </div>
        <button onClick={handleSubmit} className="mt-4 w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors">
          Hatırlatma Ekle
        </button>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  isOpen,
  task,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  task: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { isDark } = useTheme();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-sm rounded-2xl p-5 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <p className={`text-sm ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          <strong>{task}</strong> silinsin mi?
        </p>
        <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Bu işlem geri alınamaz.</p>
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className={`flex-1 py-2.5 text-sm font-medium rounded-lg border ${isDark ? 'border-dark-border text-dark-text hover:bg-dark-border' : 'border-light-border text-light-text hover:bg-light-bg'}`}>İptal</button>
          <button onClick={onConfirm} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors">Sil</button>
        </div>
      </div>
    </div>
  );
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diff = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function HatirlatmalarPage() {
  const { isDark } = useTheme();
  const { reminders, isLoading, addReminder, toggleDone, deleteReminder } = useReminders();
  const [filter, setFilter] = useState('Tümü');
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; task: string } | null>(null);

  const filtered = useMemo(() => {
    if (filter === 'Tümü') return reminders;
    return reminders.filter((item) => {
      const d = daysUntil(item.dueDate);
      if (filter === 'Acil') return d <= 3 && d >= 0 && !item.isDone;
      if (filter === 'Bu Hafta') return d <= 7 && d >= 0 && !item.isDone;
      return true;
    });
  }, [reminders, filter]);

  const getBadge = (days: number, done: boolean) => {
    if (done) return { label: 'Tamamlandı', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
    if (days < 0) return { label: 'Gecikti', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    if (days <= 3) return { label: 'ACİL', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    if (days <= 7) return { label: `${days} Gün`, color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' };
    return { label: `${days} Gün`, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' };
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-orange-500">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-time-line text-white text-lg" />
            </div>
          </div>
          <div>
            <h1 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Hatırlatıcılar
            </h1>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {reminders.filter((r) => !r.isDone).length} aktif hatırlatma
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="w-full sm:w-auto py-2.5 px-4 bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line" />
          </div>
          Yeni Hatırlatma
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {['Tümü', 'Acil', 'Bu Hafta'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-orange-500 text-white'
                : isDark
                ? 'bg-dark-card border border-dark-border text-dark-muted hover:bg-dark-border'
                : 'bg-white border border-light-border text-light-muted hover:bg-light-bg'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className="divide-y dark:divide-dark-border divide-light-border">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
                <div className="h-12 w-12 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List */}
      {!isLoading && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <div className="divide-y dark:divide-dark-border divide-light-border">
            {filtered.map((item) => {
              const d = daysUntil(item.dueDate);
              const badge = getBadge(d, item.isDone);
              return (
                <div
                  key={item.id}
                  className={`p-4 flex items-center gap-4 ${isDark ? 'bg-dark-card' : 'bg-white'}`}
                >
                  <button
                    onClick={() => toggleDone(item.id, item.isDone)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                      item.isDone
                        ? 'bg-primary-500 border-primary-500'
                        : d <= 3 && d >= 0
                        ? 'border-red-500 hover:border-red-400'
                        : isDark
                        ? 'border-dark-border hover:border-dark-text'
                        : 'border-light-border hover:border-light-text'
                    }`}
                  >
                    {item.isDone && <i className="ri-check-line text-white text-xs" />}
                  </button>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    d <= 3 && !item.isDone ? 'bg-red-100 dark:bg-red-900/30' :
                    d <= 7 && !item.isDone ? 'bg-orange-100 dark:bg-orange-900/30' :
                    item.isDone ? 'bg-green-100 dark:bg-green-900/30' :
                    'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <i className={`${
                      d <= 3 && !item.isDone ? 'ri-alarm-warning-line text-red-500' :
                      d <= 7 && !item.isDone ? 'ri-time-line text-orange-500' :
                      item.isDone ? 'ri-check-line text-green-500' :
                      'ri-calendar-line text-gray-500'
                    } text-lg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${item.isDone ? 'line-through opacity-50' : ''} ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.task}
                    </p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.dueDate} · {item.dueTime}
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap ${badge.color}`}>
                    {badge.label}
                  </span>
                  <button
                    onClick={() => setDeleteTarget({ id: item.id, task: item.task })}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20 text-red-500 hover:bg-red-200 transition-colors flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-dark-border flex items-center justify-center">
            <i className="ri-calendar-check-line text-2xl text-gray-400 dark:text-dark-muted" />
          </div>
          <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            {filter === 'Tümü' ? 'Henüz hatırlatma yok' : 'Bu filtreye uygun hatırlatma yok'}
          </p>
        </div>
      )}

      {/* Modals */}
      <AddReminderModal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={(data) => addReminder(data)}
      />
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        task={deleteTarget?.task || ''}
        onConfirm={() => {
          if (deleteTarget) {
            deleteReminder(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
